import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './TopA.module.scss';

// --- Sub-components for Visuals ---

const ChatGraph: React.FC<{ variant?: 'pink' | 'bar' }> = ({ variant = 'pink' }) => (
  <div className={styles.chatGraph}>
    {variant === 'pink' ? (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #f0f0f0', marginBottom: '15px' }}>
          <div style={{ fontSize: '0.75rem', color: '#1a1a1a', fontWeight: 800 }}>週間CPA推移</div>
          <div style={{ fontSize: '0.65rem', color: '#999', marginTop: '2px' }}>CPA (獲得単価) - 直近7日間</div>
        </div>
        <div style={{ position: 'relative', height: '120px' }}>
          <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', borderBottom: '1px solid #f9f9f9', borderTop: '1px solid #f9f9f9' }}>
            <div style={{ position: 'absolute', top: '50%', width: '100%', borderTop: '1px dashed #f5f5f5' }} />
          </div>
          <svg viewBox="0 0 400 120" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible' }}>
            <motion.path
              d="M0,100 C80,98 120,85 160,70 C240,40 320,50 400,45"
              fill="none"
              stroke="#ff3366"
              strokeWidth="1.5"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 2, ease: "easeInOut" }}
            />
            {[0, 160, 320, 400].map((x, i) => (
              <motion.circle
                key={i}
                cx={x}
                cy={[100, 70, 46, 45][i]}
                r="3"
                fill="#fff"
                stroke="#ff3366"
                strokeWidth="1.5"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1 + i * 0.3 }}
              />
            ))}
          </svg>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '0.6rem', color: '#bbb' }}>
          <span>月</span><span>水</span><span>金</span><span>日</span>
        </div>
      </div>
    ) : (
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #f0f0f0', marginBottom: '15px' }}>
          <div style={{ fontSize: '0.75rem', color: '#1a1a1a', fontWeight: 800 }}>日次予算配分案</div>
          <div style={{ fontSize: '0.65rem', color: '#999', marginTop: '2px' }}>AI推奨の媒体別最適配分</div>
        </div>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', height: '120px', padding: '0 10px' }}>
          {[
            { label: 'Ggl', h: 45 },
            { label: 'Meta', h: 75 },
            { label: 'Yaho', h: 50 },
            { label: 'Tok', h: 95, active: true },
            { label: 'Oth', h: 65 }
          ].map((item, i) => (
            <div key={i} style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '100%', flex: 1, display: 'flex', alignItems: 'flex-end', position: 'relative' }}>
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${item.h}%` }}
                  transition={{ delay: i * 0.1, duration: 1.2, ease: "circOut" }}
                  style={{
                    width: '100%',
                    background: item.active ? 'linear-gradient(to top, #ff3366, #ff80a0)' : '#f2f2f2',
                    borderRadius: '4px 4px 0 0',
                    boxShadow: item.active ? '0 4px 12px rgba(255, 51, 102, 0.2)' : 'none'
                  }}
                />
              </div>
              <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#999' }}>{item.label}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 'auto', textAlign: 'center', paddingTop: '10px' }}>
          <span style={{ fontSize: '0.6rem', color: '#bbb' }}>最適化された媒体別予算比率</span>
        </div>
      </div>
    )}
  </div>
);

const CardGraphBackground: React.FC<{ variant: 'up' | 'down' | 'zero' }> = ({ variant }) => {
  if (variant === 'up') return (
    <div className={styles.miniChartBackground}>
      <svg viewBox="0 0 200 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        <path d="M0,80 Q50,75 100,50 T200,10" fill="none" stroke="#ff3366" strokeWidth="8" />
      </svg>
    </div>
  );
  if (variant === 'down') return (
    <div className={styles.miniChartBackground}>
      <svg viewBox="0 0 200 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        <path d="M0,10 Q50,20 100,60 T200,90" fill="none" stroke="#00c6ff" strokeWidth="8" />
      </svg>
    </div>
  );
  return (
    <div className={styles.miniChartBackground} style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', padding: '0 20px' }}>
      {[20, 40, 60, 30, 10, 5].map((h, i) => (
        <div key={i} style={{ flex: 1, height: `${h}%`, background: '#eee', borderRadius: '4px' }} />
      ))}
    </div>
  );
};

const ThinkingDots: React.FC = () => (
  <div className={styles.thinking}>
    <span /><span /><span />
  </div>
);

const AnimationDiagnosis: React.FC = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex(prev => (prev === 0 ? 1 : 0));
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className={styles.demoContainer}>
      <div className={styles.suggestionDemo}>
        <div className={styles.sidebar}>
          {[0, 1, 2].map(i => (
            <div key={i} className={styles.sideItem}>
              <div className={styles.dot} />
              <div className={styles.line} />
            </div>
          ))}
          <motion.div
            className={styles.activeIndicator}
            animate={{ y: index === 0 ? 0 : 52 }}
            transition={{ duration: 0.8, ease: "anticipate" }}
          />
        </div>
        <div className={styles.suggestContent}>
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              className={styles.suggestCard}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
            >
              <h4>{index === 0 ? '予算配分の最適化案' : '広告バナーの修正案'}</h4>
              <p>
                {index === 0
                  ? 'Google検索広告の予算を15%増額し、TikTok広告へ遷移させることで全体のCPAを維持しつつ最大効率を導き出します。'
                  : '過去の成果データを元に、CTRが高い要素を組み合わせた新クリエイティブ案を5件生成しました。'}
              </p>
              <div className={styles.applyBtn}>詳細を見る</div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const AnimationSuggestion: React.FC = () => {
  const [step, setStep] = useState<'plan' | 'clicking_plan' | 'applying' | 'proposal' | 'clicking_proposal' | 'publishing' | 'done'>('plan');

  useEffect(() => {
    let isMounted = true;
    const loop = async () => {
      if (!isMounted) return;
      setStep('plan');
      await new Promise(r => setTimeout(r, 3500));

      setStep('clicking_plan');
      await new Promise(r => setTimeout(r, 400));

      setStep('applying');
      await new Promise(r => setTimeout(r, 2000));

      setStep('proposal');
      await new Promise(r => setTimeout(r, 4000));

      setStep('clicking_proposal');
      await new Promise(r => setTimeout(r, 400));

      setStep('publishing');
      await new Promise(r => setTimeout(r, 2000));

      setStep('done');
      await new Promise(r => setTimeout(r, 4000));

      if (isMounted) loop();
    };
    loop();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className={styles.demoContainer}>
      <div className={styles.suggestionStepDemo}>
        <AnimatePresence mode="wait">
          {step === 'plan' || step === 'clicking_plan' ? (
            <motion.div key="plan" className={styles.suggestCard} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <span className={styles.stepLabel}>STEP 01: 分析・提案</span>
              <h4>運用状況の診断完了</h4>
              <p>現在の各媒体のパフォーマンスを分析しました。獲得効率を最大化するための予算配分案を生成します。</p>
              <motion.div
                className={styles.applyBtn}
                animate={{ scale: step === 'clicking_plan' ? 0.92 : 1 }}
              >
                提案してもらう
              </motion.div>
            </motion.div>
          ) : (step === 'applying' || step === 'publishing') ? (
            <motion.div key="loading" className={styles.loadingBox} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className={styles.spinner} />
              <p>{step === 'applying' ? '提案を作成中...' : '広告を出稿中...'}</p>
            </motion.div>
          ) : step === 'proposal' || step === 'clicking_proposal' ? (
            <motion.div key="proposal" className={styles.suggestCard} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <span className={styles.stepLabel}>STEP 02: 戦略確定</span>
              <h4>予算配分の最適化案</h4>
              <div className={styles.budgetChangeList}>
                <div className={styles.budgetItem}>
                  <span>Google Ads</span>
                  <span className={styles.valTrend}>40% → <span className={styles.pinkHighlight}>30%</span></span>
                </div>
                <div className={styles.budgetItem}>
                  <span>TikTok Ads</span>
                  <span className={styles.valTrend}>5% → <span className={styles.pinkHighlight}>15%</span></span>
                </div>
              </div>
              <p style={{ marginTop: '12px' }}>こちらの内容で出稿してよろしいですか？</p>
              <motion.div
                className={styles.primaryBtnSmall}
                animate={{ scale: step === 'clicking_proposal' ? 0.92 : 1 }}
              >
                出稿
              </motion.div>
            </motion.div>
          ) : step === 'done' ? (
            <motion.div key="done" className={styles.doneBox} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
              <div className={styles.pinkCheck}>✔</div>
              <h4>出稿が完了しました！</h4>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
};

const AnimationVerification: React.FC = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const sequence = async () => {
      if (!isMounted) return;
      setStep(0);
      await new Promise(r => setTimeout(r, 1000));
      if (!isMounted) return;
      setStep(1); // Date 1 & Action Detail
      await new Promise(r => setTimeout(r, 2000));
      if (!isMounted) return;
      setStep(2); // Line growth
      await new Promise(r => setTimeout(r, 1500));
      if (!isMounted) return;
      setStep(3); // Date 2
      await new Promise(r => setTimeout(r, 1000));
      if (!isMounted) return;
      setStep(4); // Result Card
      await new Promise(r => setTimeout(r, 4000));

      if (isMounted) sequence();
    };
    sequence();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className={styles.demoContainer}>
      <div className={styles.verifDemoWithSidebar}>
        <div className={styles.verifSidebar}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} className={styles.verifSideItem} />
          ))}
        </div>
        <div className={styles.verifMainContent}>
          <div className={styles.verifTimelineParallel}>
            {/* Step 1: Action */}
            <div className={styles.verifRow}>
              <div className={styles.verifIndicator}>
                <motion.div
                  className={styles.verifDot}
                  initial={{ scale: 0 }}
                  animate={{ scale: step >= 1 ? 1 : 0 }}
                />
                {/* Connection Line now inside the first indicator */}
                <div className={styles.verifLineTrack}>
                  <motion.div
                    className={styles.verifLineFill}
                    initial={{ height: 0 }}
                    animate={{ height: step >= 2 ? '100%' : 0 }}
                    transition={{ duration: 1.2 }}
                  />
                </div>
              </div>
              <div className={styles.verifBody}>
                <motion.div
                  className={styles.verifDate}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: step >= 1 ? 1 : 0 }}
                >
                  2026年1月10日(土) 自動出稿
                </motion.div>
                <motion.div
                  className={styles.verifActionInfo}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: step >= 1 ? 1 : 0, y: step >= 1 ? 0 : 10 }}
                >
                  予算配分の変更を実施しました！<br />
                  <span className={styles.sub}>Google 40%→30% / TikTok 5%→15%</span>
                </motion.div>
              </div>
            </div>

            {/* Step 2: Verification */}
            <div className={styles.verifRow}>
              <div className={styles.verifIndicator}>
                <motion.div
                  className={styles.verifCheck}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: step >= 3 ? 1 : 0, scale: step >= 3 ? 1 : 0 }}
                >
                  ✔
                </motion.div>
              </div>
              <div className={styles.verifBody}>
                <motion.div
                  className={styles.verifDate}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: step >= 3 ? 1 : 0 }}
                >
                  2026年1月24日(土) AI自動検証
                </motion.div>
                <motion.div
                  className={styles.verifResultCard}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: step >= 4 ? 1 : 0, scale: step >= 4 ? 1 : 0.9 }}
                >
                  <div className={styles.goodLabel}>Good!</div>
                  <div className={styles.verifMsg}>CTRの増加が確認されました！</div>
                  <div className={styles.verifStats}>1.8% → <span className={styles.verifPink}>2.4%</span></div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LegacyDashboard: React.FC = () => {
  const rows = [
    { label: 'Campaign_01', imp: '1.2M', cli: '1.4k', ctr: '1.2%', cpm: '1.2k', cpc: '85', cv: '42', cpa: '2.4k', cvr: '3.0%' },
    { label: 'Campaign_02', imp: '850k', cli: '6.2k', ctr: '0.7%', cpm: '2.1k', cpc: '240', cv: '12', cpa: '12.8k', cvr: '0.1%' },
    { label: 'Remarketing_A', imp: '420k', cli: '10k', ctr: '2.4%', cpm: '0.8k', cpc: '32', cv: '156', cpa: '2.1k', cvr: '1.5%' },
    { label: 'Search_Brand', imp: '120k', cli: '9.8k', ctr: '8.1%', cpm: '4.2k', cpc: '52', cv: '342', cpa: '0.8k', cvr: '12%' },
    { label: 'Display_Int', imp: '3.2M', cli: '14k', ctr: '0.4%', cpm: '0.6k', cpc: '142', cv: '28', cpa: '6.4k', cvr: '0.2%' },
    { label: 'Video_Max', imp: '5.1M', cli: '42k', ctr: '0.8%', cpm: '1.1k', cpc: '125', cv: '84', cpa: '7.6k', cvr: '0.2%' },
  ];

  return (
    <div className={styles.legacyDashboard}>
      <div className={styles.legacyHeader}>
        <div className={styles.legacyNav} />
        <div className={styles.legacyUser} />
      </div>
      <div className={styles.legacyBody}>
        <div className={styles.legacySidebar}>
          {[1, 2, 3, 4, 5, 6, 7].map(i => <div key={i} className={styles.sideItem} />)}
        </div>
        <div className={styles.legacyMain}>
          <div className={styles.legacyStats}>
            <div className={styles.legacyStatBox}><span>CPA</span><strong>¥4,821</strong></div>
            <div className={styles.legacyStatBox}><span>Cost</span><strong>¥1.2M</strong></div>
            <div className={styles.legacyStatBox}><span>CV</span><strong>248</strong></div>
            <div className={styles.legacyStatBox}><span>ROAS</span><strong>240%</strong></div>
          </div>
          <div className={styles.legacyTable}>
            <div className={styles.tableHeaderCompact}>
              <span>Name</span><span>Imp</span><span>Cli</span><span>CTR</span><span>CPM</span><span>CPC</span><span>CV</span><span>CPA</span><span>CVR</span>
            </div>
            {rows.map((row, i) => (
              <div key={i} className={styles.tableRowCompact}>
                <span>{row.label.slice(0, 6)}..</span>
                <span>{row.imp}</span><span>{row.cli}</span><span>{row.ctr}</span>
                <span>{row.cpm}</span><span>{row.cpc}</span><span>{row.cv}</span>
                <span>{row.cpa}</span><span>{row.cvr}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const AibexExperienceChat: React.FC = () => {
  return (
    <div className={styles.aibexExpChat}>
      <div className={styles.browserFrameMini}>
        <div className={styles.browserHeaderMini}>
          <div className={styles.dotMini} /><div className={styles.dotMini} /><div className={styles.dotMini} />
        </div>
        <div className={styles.browserBodyMini}>
          <div className={styles.expBubbleStack}>
            <div className={styles.expBubbleAiMini}>
              今月は渋谷区エリアのコンバージョン率（CVR）が1.25%、前月比+15%と非常に順調です。地域特化キーワードの反応が良いことが理由と考えられます。
            </div>
            <div className={styles.expBubbleAiMini}>
              一方で、港区エリアはCPAが10万円を超え効率が悪化しているため、こちらの予算を<strong>15%</strong>渋谷へ配分変更しませんか？ これにより、全体のCPAを約8%改善しつつ、港区エリアへの認知も一定獲得できます。
            </div>
            <div className={styles.expBubbleAiMini}>
              その他の媒体の変動は誤差の範囲内ですので、今は気にする必要はありません。
            </div>
            <div className={styles.expBubbleUserMini}>
              それで進めてください！
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Page ---

const TopA: React.FC = () => {
  const navigate = useNavigate();
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [typingPlaceholder, setTypingPlaceholder] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [isPressingSend, setIsPressingSend] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const fullSequence = [
    { type: 'ai', text: '本日のGoogle広告の成果は、目標CPA比+15.4%です。一部のクリエイティブで劣化が見られます。' },
    { type: 'graph', variant: 'pink' },
    { type: 'user', text: '具体的にどのバナーを差し替えればいいですか？' },
    { type: 'ai', text: '「夏のキャンペーン_B」セットの3枚です。CTRが前週比で20%下落しています。改善案を作成しました。', thumbnail: true },
    { type: 'user', text: '早速この案を実施してください。' },
    { type: 'progress' },
    { type: 'ai', text: '完了しました。' },
    { type: 'ai', text: 'さらに予算の最適化も可能です。いかがでしょうか？' },
    { type: 'user', text: 'ぜひ！予算の最適化案を教えてください。' },
    { type: 'graph', variant: 'bar' },
    { type: 'ai', text: 'はい。現在TikTok広告の獲得効率がGoogle広告を25%上回っています。この余剰予算をGoogle広告の検索キャンペーンに5%増強することで、全体のCPAを維持しつつ、コンバージョン数12%増が期待できます。' },
  ];

  useEffect(() => {
    let isMounted = true;
    const autoLoop = async () => {
      if (!isMounted) return;
      setIsResetting(false);
      setChatMessages([]);

      setShowWelcome(true);
      await new Promise(r => setTimeout(r, 2200));
      setShowWelcome(false);
      await new Promise(r => setTimeout(r, 800));
      setTypingPlaceholder('');

      for (const msg of fullSequence) {
        if (!isMounted) return;

        if (msg.type === 'user' && msg.text) {
          for (let i = 0; i <= msg.text.length; i++) {
            if (!isMounted) return;
            setTypingPlaceholder(msg.text.slice(0, i));
            await new Promise(r => setTimeout(r, 40));
          }
          await new Promise(r => setTimeout(r, 600));
          setIsPressingSend(true);
          await new Promise(r => setTimeout(r, 150));
          setIsPressingSend(false);
          setChatMessages(prev => [...prev, { id: `user-${Date.now()}`, type: 'user', text: msg.text! }]);
          setTypingPlaceholder('');
          await new Promise(r => setTimeout(r, 1400));
        } else if (msg.type === 'graph') {
          setChatMessages(prev => [...prev, { id: `graph-${msg.variant}-${Date.now()}`, type: 'graph', variant: msg.variant }]);
          await new Promise(r => setTimeout(r, 2200));
        } else if (msg.type === 'ai' && msg.text) {
          const aiId = `ai-${Date.now()}`;
          setChatMessages(prev => [...prev, { id: aiId, ...msg, text: '', thinking: true, thumbnail: false }]);
          await new Promise(r => setTimeout(r, 1200));

          for (let i = 0; i <= msg.text.length; i++) {
            if (!isMounted) return;
            setChatMessages(prev => prev.map(m => m.id === aiId ? { ...m, text: msg.text!.slice(0, i), thinking: false } : m));
            await new Promise(r => setTimeout(r, 25));
          }

          if (msg.thumbnail) {
            await new Promise(r => setTimeout(r, 800)); // Show thumbnails after text is typed
            setChatMessages(prev => prev.map(m => m.id === aiId ? { ...m, thumbnail: true } : m));
          }

          await new Promise(r => setTimeout(r, 2200));
        } else if (msg.type === 'progress') {
          const progressId = `progress-${Date.now()}`;
          setChatMessages(prev => [...prev, { id: progressId, type: 'progress', progress: 0 }]);
          for (let i = 0; i <= 100; i += 5) {
            if (!isMounted) return;
            setChatMessages(prev => prev.map(m => m.id === progressId ? { ...m, progress: i } : m));
            await new Promise(r => setTimeout(r, 50));
          }
          await new Promise(r => setTimeout(r, 400));
          setChatMessages(prev => prev.map(m => m.id === progressId ? { ...m, done: true } : m));
          await new Promise(r => setTimeout(r, 1200));
        }
      }

      await new Promise(r => setTimeout(r, 6000));
      if (isMounted) {
        setIsResetting(true);
        await new Promise(r => setTimeout(r, 1000));
        autoLoop();
      }
    };
    autoLoop();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (chatEndRef.current) {
      const container = chatEndRef.current.closest(`.${styles.chatScrollWrapper}`);
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [chatMessages, typingPlaceholder]);

  return (
    <div className={styles.containerWrap}>
      <div className={styles.backgroundGlow}>
        <div className={styles.glowBlue} />
        <div className={styles.glowPink} />
      </div>

      <header className={styles.header}>
        <div className={styles.logoArea}>
          <img src="/images/logo_aibex_line_b.svg" alt="Aibex" className={styles.logo} />
          <span className={styles.logoKatakana}>マーケティングに特化したAIエージェント「Aibex（アイベックス）」</span>
        </div>
        <div className={styles.navButtons}>
          <button className={styles.loginBtn} onClick={() => navigate('/login')}>ログイン</button>
          <button className={styles.signUpBtn} onClick={() => navigate('/pre-register')}>無料ではじめる</button>
        </div>
        <button
          className={`${styles.hamburger} ${isMenuOpen ? styles.open : ''}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span /><span /><span />
        </button>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className={styles.mobileMenu}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <button className={styles.loginBtn} onClick={() => navigate('/login')}>ログイン</button>
              <button className={styles.signUpBtn} onClick={() => navigate('/pre-register')}>無料ではじめる</button>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main>
        <section className={styles.heroSection}>
          <div className={styles.mockupContainer}>


            <div className={styles.browserFrame}>
              <div className={styles.browserHeader}>
                <div className={styles.dot} /><div className={styles.dot} /><div className={styles.dot} />
                <div className={styles.addressBar} />
              </div>
              <div className={styles.browserBody}>
                <AnimatePresence>
                  {showWelcome && (
                    <motion.div className={styles.welcomeIntroOverlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <motion.h2 initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 }}>Aibexへようこそ。</motion.h2>
                    </motion.div>
                  )}
                </AnimatePresence>
                <div className={styles.chatArea}>
                  <div className={styles.chatScrollWrapper}>
                    <div className={styles.chatMessages}>
                      <AnimatePresence mode="popLayout">
                        {chatMessages.map(msg => (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                            className={msg.type === 'graph' ? styles.standaloneGraph : (msg.type === 'progress' ? styles.progressContainer : styles.chatWrap)}
                          >
                            {msg.type === 'graph' ? (
                              <ChatGraph variant={msg.variant as 'pink' | 'bar'} />
                            ) : msg.type === 'progress' ? (
                              <div className={styles.progressBox}>
                                <div className={styles.progressHeader}>
                                  <span className={styles.statusLabel}>
                                    {msg.progress < 40 ? '出稿準備中...' : (msg.progress < 100 ? '出稿中...' : '出稿完了')}
                                  </span>
                                </div>
                                <div className={styles.progressMain}>
                                  <div className={styles.progressBar}>
                                    <motion.div
                                      className={styles.progressFill}
                                      initial={{ width: 0 }}
                                      animate={{ width: `${msg.progress}%` }}
                                    />
                                  </div>
                                  {msg.done && (
                                    <motion.span
                                      className={styles.pinkCheck}
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                    >
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }}>
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    </motion.span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className={msg.type === 'ai' ? styles.aiWrap : styles.userWrap}>
                                {/* Only render bubble if there is text or thinking */}
                                {(msg.text || msg.thinking) && (
                                  <div className={`${styles.chatBubble} ${msg.type === 'ai' ? styles.ai : styles.user}`}>
                                    {msg.thinking ? <ThinkingDots /> : msg.text}
                                  </div>
                                )}
                                {msg.thumbnail && (
                                  <motion.div
                                    className={styles.bannerThumbnailStackOutside}
                                    initial="hidden"
                                    animate="visible"
                                    variants={{
                                      hidden: { opacity: 0 },
                                      visible: {
                                        opacity: 1,
                                        transition: { staggerChildren: 0.15 }
                                      }
                                    }}
                                  >
                                    {[
                                      { bg: 'linear-gradient(135deg, #FF008033, #7928CA33)', label: 'SALE' },
                                      { bg: 'linear-gradient(135deg, #0070F333, #00C6FF33)', label: 'NEW' },
                                      { bg: 'linear-gradient(135deg, #F5A62333, #F76B1C33)', label: 'HOT' }
                                    ].map((item, i) => (
                                      <motion.div
                                        key={i}
                                        className={styles.bannerThumb}
                                        variants={{
                                          hidden: { scale: 0, opacity: 0 },
                                          visible: {
                                            scale: 1,
                                            opacity: 1,
                                            transition: { type: 'spring', stiffness: 400, damping: 20 }
                                          }
                                        }}
                                      >
                                        <div className={styles.bannerThumbImg} style={{ background: item.bg }} />
                                      </motion.div>
                                    ))}
                                  </motion.div>
                                )}
                              </div>
                            )}
                          </motion.div>
                        ))}
                      </AnimatePresence>
                      <div ref={chatEndRef} />
                    </div>
                  </div>

                  <div className={styles.chatInputContainer}>
                    <div className={styles.chatInputArea}>
                      <div className={`${styles.inputText} ${!typingPlaceholder ? styles.placeholder : ''}`}>
                        {typingPlaceholder || 'AIに質問する...'}
                      </div>
                      <button className={`${styles.sendBtn} ${isPressingSend ? styles.active : ''}`}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <AnimatePresence>
                    {isResetting && (
                      <motion.div className={styles.resetOverlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <div className={styles.resetSpinner} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.heroContent}>
            <h1 className={styles.mainCopy}>
              広告成果を最大化する<br />
              AIエージェント
            </h1>
            <p className={styles.subCopy}>分析・改善から出稿まで、<br className={styles.mobileBr} />AIがあなたをナビゲートする新時代の広告運用。</p>
          </div>

          <div className={styles.ctaGroup}>
            <button className={styles.primaryBtn} onClick={() => navigate('/pre-register')}>無料ではじめる</button>
            <button className={styles.secondaryBtn} onClick={() => navigate('/contact')}>お問い合わせ</button>
          </div>

          <div className={styles.clientLogos}>
            <img src="/images/logo_ad/GA4_Logo.png" alt="GA4" className={styles.vendorLogo} style={{ height: '22px', transform: 'scale(2.0)', margin: '0 20px' }} />
            <img src="/images/logo_ad/Google_Ads.png" alt="Google" className={styles.vendorLogo} style={{ height: '24px' }} />
            <img src="/images/logo_ad/Meta.png" alt="Meta" className={styles.vendorLogo} style={{ height: '20px' }} />
            <img src="/images/logo_ad/YAHOO.png" alt="Yahoo" className={styles.vendorLogo} style={{ height: '16px' }} />
            <img src="/images/logo_ad/LINE.png" alt="LINE" className={styles.vendorLogo} style={{ height: '18px' }} />
            <img src="/images/logo_ad/Microsoft.png" alt="Microsoft" className={styles.vendorLogo} style={{ height: '20px', transform: 'scale(2.6)', margin: '0 40px' }} />
            <img src="/images/logo_ad/TikTok.png" alt="TikTok" className={styles.vendorLogo} style={{ height: '22px' }} />
          </div>
        </section>

        <section className={styles.challengesSection}>
          <div className={styles.sectionHeader}>
            <img src="/images/logo_star.svg" alt="star" className={styles.headerIcon} />
            <h2>広告運用における課題</h2>
          </div>
          <h3 className={styles.sectionMainCopy}>
            成果を伸ばせない理由は、<br />
            運用の“手間と複雑さ”にあります。
          </h3>

          <div className={styles.challengesScrollWrapper}>
            <div className={styles.challengesGrid}>
              <div className={styles.challengeCard}>
                <div className={styles.iconWrapper}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                </div>
                <h4>何からすべきか不明</h4>
                <p>データはあるのに「どこから手をつければいいのか」分からず、改善が前に進まない。</p>
              </div>
              <div className={styles.challengeCard}>
                <div className={styles.iconWrapper}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                </div>
                <h4>分析の時間が足りない</h4>
                <p>日々の運用に追われ、本来向き合うべき分析や戦略を考える余裕がない。</p>
              </div>
              <div className={styles.challengeCard}>
                <div className={styles.iconWrapper}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"></path><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"></path></svg>
                </div>
                <h4>根拠に自信がない</h4>
                <p>経験や勘に頼った運用になり、<br className={styles.mobileBr} />判断にいつも不安が残ってしまう。</p>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.solutionSection}>
          <div className={styles.solutionHeaderArea}>
            <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}>
              その悩み、AIエージェント「Aibex」が<br />すべて解決します。
            </motion.h2>
            <p style={{ fontSize: '1.2rem', color: '#666', marginBottom: '40px' }}>
              GA4や広告データを多角的に分析。これまで数時間かかっていた分析・提案を自動化します。
            </p>
            <div className={styles.impactStatArea}>
              <span className={styles.prefix}>年間</span>
              <span className={styles.mainValue}>5</span>
              <span className={styles.suffix}>億円以上の広告運用実績</span>
              <span className={styles.disclaimer}>*2025年度Aibex運営会社(株式会社Inovis)運用実績</span>
            </div>
          </div>

          <div className={styles.solutionScrollWrapper}>
            <div className={styles.solutionGrid}>
              <div className={styles.solutionCarousel}>
                <div className={styles.solutionCard}>
                  <CardGraphBackground variant="up" />
                  <span className={styles.cardLabel}>コンバージョン率（CVR）</span>
                  <div className={styles.metricValue}>
                    <span className={styles.smallLabel}>平均</span>
                    <span className={styles.pinkText}>5%向上</span>
                  </div>
                  <p className={styles.description}>
                    過去の運用データに基づき、獲得効率（CVR/ROAS）を最大化する入札・配信設定をAIが自動で導き出します。
                  </p>
                </div>
                <div className={styles.solutionCard}>
                  <CardGraphBackground variant="down" />
                  <span className={styles.cardLabel}>データ分析工数</span>
                  <div className={styles.metricValue}>
                    <span className={styles.smallLabel}>平均</span>
                    <span className={styles.blueText}>90%削減</span>
                  </div>
                  <p className={styles.description}>
                    24時間365日、AIが常に数値を監視。<br />ボトルネック特定から要因分析までを一<br />瞬で完了させます。
                  </p>
                </div>
                <div className={styles.solutionCard}>
                  <CardGraphBackground variant="zero" />
                  <span className={styles.cardLabel}>レポート作成</span>
                  <div className={styles.metricValue}>
                    <span className={styles.smallLabel}>資料作成時間</span>
                    <span className={styles.gradientText}>ゼロへ</span>
                  </div>
                  <p className={styles.description}>
                    報告資料をボタン一つで自動生成。定型<br />作業から解放され、より本質的なマーケ<br />戦略策定に集中できます。
                  </p>
                </div>
              </div>

              <div className={`${styles.solutionCard} ${styles.comparison} ${styles.withDecor}`}>
                <CardGraphBackground variant="zero" />
                <div className={styles.metricHeaderInline}>
                  <div className={styles.metricHeaderMain}>
                    <span className={styles.cardLabel}>運用満足度</span>
                    <div className={styles.metricValue}>
                      <span className={styles.smallLabel}>平均</span>
                      <span className={styles.pinkText}>97.6%</span>
                    </div>
                  </div>
                  <div className={styles.metricDescription}>
                    進化し続けるDXツールが、時に現場を疲弊させていませんか？<br />
                    Aibexは、煩雑な分析や操作からあなたを解放します。<br />
                    業務を削ぎ落とし、最短距離で成果へ導く、唯一のAIエージェントです。
                  </div>
                </div>

                <div className={styles.comparisonGrid}>
                  <div className={styles.comparisonItem}>
                    <div className={styles.compLabel}>従来の運用（一般的なDXツール）</div>
                    <div className={styles.compVisual}>
                      <LegacyDashboard />
                    </div>
                    <p className={styles.compCopy}>
                      管理画面の数字に、疲れていませんか？<br />
                      分析に数時間、設定に数時間。<br />
                      機能が豊富で全てを使いこなせない。
                    </p>
                  </div>

                  <div className={styles.comparisonItem}>
                    <div className={styles.compLabel}>Aibexによる次世代の運用体験</div>
                    <div className={styles.compVisual}>
                      <AibexExperienceChat />
                    </div>
                  </div>
                </div>

                <div className={styles.comparisonSummaryShort}>
                  AIが膨大なデータを分析。<br className={styles.mobileBr} />人は判断するだけ。
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.midCtaSection}>
          <div className={styles.largeCtaCard}>
            <h3>AIがあなたの広告運用を<br />劇的に変えます。</h3>
            <div className={styles.ctaButtonWrapper}>
              <button className={`${styles.primaryBtn} ${styles.accent}`} onClick={() => navigate('/register')}>無料ではじめる</button>
            </div>
          </div>
        </section>

        <section className={styles.intelligenceSection}>
          <div className={styles.sectionHeader}>
            <img src="/images/logo_star.svg" alt="star" className={styles.headerIcon} />
            <h2>Aibexにできること</h2>
          </div>
          <div style={{ textAlign: 'center', marginBottom: '0' }}>
            <h3 style={{ fontSize: '2.5rem', fontWeight: 950 }}>あなたのマーケティング業務を加速させる、<br />3つのインテリジェンス</h3>
          </div>

          <div className={styles.featureItem}>
            <div className={styles.featureText}>
              <span className={styles.label}>Intelligence 01</span>
              <h3>AI診断機能</h3>
              <p>AIが主要指標を24時間監視。<br className={styles.mobileBr} />無駄なコストや改善余地を一瞬で発見。</p>
              <div className={styles.featureDetail}>
                CVR向上に貢献しているクリエイティブや、ボトルネックとなっている要因を自動特定。<br className={styles.mobileBr} />マーケティング初心者でも「次の一手」が明確になります。
              </div>
            </div>
            <div className={styles.featureVisual}>
              <AnimationDiagnosis />
            </div>
          </div>

          <div className={`${styles.featureItem} ${styles.reverse}`}>
            <div className={styles.featureText}>
              <span className={styles.label}>Intelligence 02</span>
              <h3>AI提案機能</h3>
              <p>聞く・頼むをチャットで完結。<br className={styles.mobileBr} />運用のアクションまで即座に実行。</p>
              <div className={styles.featureDetail}>
                AIが課題抽出だけでなく、予算/エリア/キーワード/広告文など改善案を考えて提示します。そのままボタン一つで広告媒体への設定反映が可能です。
              </div>
            </div>
            <div className={styles.featureVisual}>
              <AnimationSuggestion />
            </div>
          </div>

          <div className={styles.featureItem}>
            <div className={styles.featureText}>
              <span className={styles.label}>Intelligence 03</span>
              <h3>施策検証機能</h3>
              <p>実施した施策の検証結果をAIが自動解析。<br className={styles.mobileBr} />PDCAを確実にまわす。</p>
              <div className={styles.featureDetail}>
                過去に行った変更が成果にどう影響したかをAIが正確に評価。成功事例のパターン化と、失敗の早期発見を可能にします。
              </div>
            </div>
            <div className={styles.featureVisual}>
              <AnimationVerification />
            </div>
          </div>

          <div className={styles.intelligenceFooter}>
            <div className={styles.chatDescription}>
              これらすべての機能を<br className={styles.mobileBr} /><span className={styles.accent}>チャットベースでAIと会話しながら、</span><br />
              マーケティング施策の改善を実行できます。
            </div>
            <p style={{ color: '#666', marginBottom: '30px', fontSize: '0.9rem', lineHeight: '1.7' }}>
              GA4、Google広告、Meta、Yahoo、LINE、Microsoft、TikTokなど、<br />
              主要なプラットフォームと連携して社内データを簡単に統合可能です。
            </p>
            <div className={styles.integrationLogos}>
              <img src="/images/logo_ad/GA4_Logo.png" alt="GA4" style={{ height: '28px', opacity: 1, transform: 'scale(2.0)', margin: '0 20px' }} />
              <img src="/images/logo_ad/Google_Ads.png" alt="Google Ads" style={{ height: '30px', opacity: 1 }} />
              <img src="/images/logo_ad/Meta.png" alt="Meta" style={{ height: '26px', opacity: 1 }} />
              <img src="/images/logo_ad/YAHOO.png" alt="Yahoo" style={{ height: '20px', opacity: 1 }} />
              <img src="/images/logo_ad/LINE.png" alt="LINE" style={{ height: '22px', opacity: 1 }} />
              <img src="/images/logo_ad/Microsoft.png" alt="Microsoft" style={{ height: '24px', opacity: 1, transform: 'scale(2.6)', margin: '0 40px' }} />
              <img src="/images/logo_ad/TikTok.png" alt="TikTok" style={{ height: '24px', opacity: 1 }} />
            </div>
          </div>
        </section>

        <section className={styles.pricingSection}>
          <div className={styles.pricingHeader}>
            <span className={styles.pricingBadge}>料金プラン</span>
            <h2>経験豊富なAIマーケターを、<br />正社員の1/8の金額で採用。</h2>
          </div>

          <div className={styles.offerBox}>
            <p className={styles.offerNotice}>
              現在テスト運用期間中につき、<br />
              3ヶ月間<span className={styles.highlight}>無料</span>でお試しいただけます。
            </p>
            <span className={styles.offerDisclaimer}>※2026年3月31日まで</span>

            <div className={styles.pricingGrid}>
              <div className={styles.priceItem}>
                <span className={styles.priceLabel}>初期費用</span>
                <div className={styles.priceValue}>
                  <span className={styles.oldPrice}>¥300,000</span>
                  <span className={styles.newPrice}>¥0</span>
                  <span className={styles.priceUnit}>/ 導入時</span>
                </div>
              </div>
              <div className={styles.priceItem}>
                <span className={styles.priceLabel}>月額利用料</span>
                <div className={styles.priceValue}>
                  <span className={styles.oldPrice}>¥39,800/月 + 広告運用費の5%</span>
                  <span className={styles.newPrice}>¥0</span>
                  <span className={styles.priceUnit}>/ 月</span>
                </div>
              </div>
            </div>
            <button className={`${styles.primaryBtn} ${styles.accent}`} style={{ margin: '0 auto', minWidth: '320px' }} onClick={() => navigate('/register')}>無料ではじめる</button>
          </div>
        </section>

        <section className={styles.faqSection}>
          <h2>よくある質問</h2>
          <div className={styles.faqList}>
            <div className={styles.faqItem}>
              <div className={styles.faqQuestion}>Q. ITやマーケティングの専門知識がなくても使えますか？</div>
              <div className={styles.faqAnswer}>A. はい、もちろんです。AIが自然な言葉での質問に応答し、専門用語も噛み砕いて説明するため、初心者の方でも安心して運用いただけます。</div>
            </div>
            <div className={styles.faqItem}>
              <div className={styles.faqQuestion}>Q. 対応している広告媒体を教えてください。</div>
              <div className={styles.faqAnswer}>A. 現在、Google広告、Meta広告（Facebook/Instagram）、Yahoo!広告に対応しています。今後さらに順次拡大予定です。</div>
            </div>
            <div className={styles.faqItem}>
              <div className={styles.faqQuestion}>Q. トライアル後に必ず契約する必要がありますか？</div>
              <div className={styles.faqAnswer}>A. いいえ、トライアル期間中にご判断いただけます。自動的に有料プランへ移行することはないため、まずはお気軽にお試しください。</div>
            </div>
            <div className={styles.faqItem}>
              <div className={styles.faqQuestion}>Q. 広告運用の経験が浅く不安です。サポート体制はありますか？</div>
              <div className={styles.faqAnswer}>A. ご安心ください。アカウント作成後、担当者が初期設定から操作レクチャーまで丁寧にサポートいたします。直感的に使えるチャット形式のため、専門知識がなくてもすぐに運用を始められます。また、運用のすべてをお任せいただける「完全代行プラン」もございますので、お気軽にご相談ください。</div>
            </div>
          </div>
        </section>

        <section className={styles.finalCtaSection}>
          <div className={styles.finalCtaContent}>
            <h3>成果に直結する、<br className={styles.mobileBr} />次世代の運用を。</h3>
            <p>分析・改善から出稿まで、<br className={styles.mobileBr} />AIがあなたをナビゲートする新時代の広告運用。</p>
            <div className={styles.ctaGroup}>
              <button className={styles.primaryBtn} onClick={() => navigate('/register')}>無料ではじめる</button>
              <button className={styles.secondaryBtn} onClick={() => navigate('/contact')}>お問い合わせ</button>
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <img src="/images/logo_aibex_line_b.svg" alt="Aibex" className={styles.footerLogo} />
        <div className={styles.footerLinks}>
          <a href="https://inovis.co.jp" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>運営会社</a>
          <span onClick={() => navigate('/privacy-policy')} style={{ cursor: 'pointer' }}>プライバシーポリシー</span>
          <span onClick={() => navigate('/terms')} style={{ cursor: 'pointer' }}>利用規約</span>
        </div>
        <p className={styles.copyright}>&copy; 2026 Inovis Inc. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default TopA;
